/**
 * Weekly-summary weeks. Weeks run Monday to Sunday, and week 1 is the week
 * holding the course's standup start date. Dates are plain YYYY-MM-DD
 * calendar days, so all arithmetic is done at UTC midnight and no time zone
 * can shift a day.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function toUtc(dateIso: string) {
  return new Date(`${dateIso}T00:00:00Z`);
}

function toIso(date: Date) {
  return date.toISOString().slice(0, 10);
}

/** The Monday on or before the given day. */
export function mondayOf(dateIso: string): string {
  const date = toUtc(dateIso);
  const back = (date.getUTCDay() + 6) % 7;
  return toIso(new Date(date.getTime() - back * DAY_MS));
}

export function addDays(dateIso: string, days: number): string {
  return toIso(new Date(toUtc(dateIso).getTime() + days * DAY_MS));
}

/** 1 for the week holding the start date. Undefined without a start date. */
export function summaryWeekNumber(weekStart: string, startDate?: string): number | undefined {
  if (!startDate) {
    return;
  }
  const diff = toUtc(mondayOf(weekStart)).getTime() - toUtc(mondayOf(startDate)).getTime();
  return Math.round(diff / (7 * DAY_MS)) + 1;
}

export type SummarySchedule = {
  /** The course's standup start date: week 1 is the week holding it. */
  startDate?: string;
  /** The course's standup end date: the last week is the week holding it. */
  endDate?: string;
  /** The last week collects the Sprint summary instead of a weekly one. */
  sprintSummary: boolean;
};

export type SummaryWindow = {
  /** The Monday of the summary's week. */
  weekStart: string;
  weekNumber: number;
  isSprint: boolean;
  /** First day the box is open. */
  opensOn: string;
  /** Last day it is on time. Undefined for the Sprint summary, which is due before the coaching call. */
  dueOn?: string;
  /** Last day it is accepted at all, late or not. */
  closesOn: string;
};

/** The week number of the course's last week, when it has an end date. */
export function lastWeekNumber(schedule: SummarySchedule) {
  if (!schedule.startDate || !schedule.endDate) {
    return;
  }
  return summaryWeekNumber(schedule.endDate, schedule.startDate);
}

function opensOn(weekStart: string, isSprint: boolean) {
  // Weekly summaries open on Friday; the Sprint summary opens on its Monday.
  return isSprint ? weekStart : addDays(weekStart, 4);
}

/**
 * The summary window for one week. Weekly summaries open Friday and are due
 * by the end of Sunday; a late one is accepted until the next summary opens.
 * The Sprint summary opens Monday of the last week and is accepted until the
 * Thursday after it, the same span a late weekly summary gets.
 */
export function summaryWindow(
  weekNumber: number,
  schedule: SummarySchedule,
): SummaryWindow | undefined {
  if (!schedule.startDate || weekNumber < 1) {
    return;
  }
  const last = lastWeekNumber(schedule);
  if (last !== undefined && weekNumber > last) {
    return;
  }
  const weekStart = addDays(mondayOf(schedule.startDate), (weekNumber - 1) * 7);
  const isSprint = schedule.sprintSummary && last === weekNumber;
  const nextIsSprint = schedule.sprintSummary && last === weekNumber + 1;
  const nextOpens = opensOn(addDays(weekStart, 7), nextIsSprint);
  return {
    weekStart,
    weekNumber,
    isSprint,
    opensOn: opensOn(weekStart, isSprint),
    dueOn: isSprint ? undefined : addDays(weekStart, 6),
    closesOn: isSprint || last === weekNumber ? addDays(weekStart, 10) : addDays(nextOpens, -1),
  };
}

/** The summary the student can send on their local `today`, if any. At most one is ever open. */
export function openSummaryWindow(
  today: string,
  schedule: SummarySchedule,
): SummaryWindow | undefined {
  const week = summaryWeekNumber(today, schedule.startDate);
  if (!week) {
    return;
  }
  for (const n of [week, week - 1]) {
    const window = summaryWindow(n, schedule);
    if (window && today >= window.opensOn && today <= window.closesOn) {
      return window;
    }
  }
}

/**
 * The summary week a coach is tracking on `today`: the open one, or else the
 * latest that has opened, so Monday to Thursday still shows last week's.
 */
export function latestSummaryWindow(
  today: string,
  schedule: SummarySchedule,
): SummaryWindow | undefined {
  const week = summaryWeekNumber(today, schedule.startDate);
  if (!week) {
    return;
  }
  const last = lastWeekNumber(schedule);
  for (let n = last !== undefined ? Math.min(week, last) : week; n >= 1; n--) {
    const window = summaryWindow(n, schedule);
    if (window && window.opensOn <= today) {
      return window;
    }
  }
}

/** The summary for this week is the Sprint summary: the course's last week, when that's switched on. */
export function isSprintWeek(weekStart: string, schedule: SummarySchedule) {
  // The week holding the end date, matching the coach feed's SQL, so a
  // cleared start date can't make the two disagree.
  return (
    schedule.sprintSummary &&
    !!schedule.endDate &&
    mondayOf(weekStart) === mondayOf(schedule.endDate)
  );
}

/** "Sprint summary" or "Weekly summary, week 1": the one label every email and page uses. */
export function summaryTitleFor(weekStart: string, schedule: SummarySchedule) {
  if (isSprintWeek(weekStart, schedule)) {
    return 'Sprint summary';
  }
  const week = summaryWeekNumber(weekStart, schedule.startDate);
  return week && week > 0 ? `Weekly summary, week ${week}` : 'Weekly summary';
}
